import {
  Button,
  Dialog,
  Typography,
} from '@sl/react-datafabric-components';
import { format, isValid } from 'date-fns';
import { FC } from 'react';

export type SubscriptionInvoice = {
  id: string;
  number?: string;
  status?: string;
  currency?: string;
  amountPaid?: number;
  createdAt?: string | Date;
  hostedInvoiceUrl?: string;
  invoicePdfUrl?: string;
};

type SubscriptionInvoicesDialogProps = {
  visible: boolean;
  loading?: boolean;
  planName?: string;
  invoices: SubscriptionInvoice[];
  onHide: () => void;
};

const formatAmount = (value?: number, currency?: string) => {
  if (typeof value !== 'number') return '-';
  const normalizedCurrency = String(currency || 'usd').toUpperCase();
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${normalizedCurrency}`;
};

export const SubscriptionInvoicesDialog: FC<SubscriptionInvoicesDialogProps> = ({
  visible,
  loading = false,
  planName,
  invoices,
  onHide,
}) => {
  return (
    <Dialog
      header={`Invoices${planName ? ` - ${planName}` : ''}`}
      visible={visible}
      onHide={onHide}
      style={{ width: '720px', maxWidth: '95vw' }}
    >
      <div className='flex flex-column gap-3'>
        <Typography className='m-0 text-600'>
          Only paid invoices are shown here.
        </Typography>

        {loading ? (
          <Typography className='text-700'>Loading invoices...</Typography>
        ) : invoices.length === 0 ? (
          <Typography className='text-700'>
            No paid invoices are available for this subscription.
          </Typography>
        ) : (
          <div className='flex flex-column gap-2'>
            {invoices.map((invoice) => {
              const createdAt = invoice?.createdAt
                ? new Date(invoice.createdAt)
                : null;
              const canOpen =
                Boolean(invoice?.invoicePdfUrl) ||
                Boolean(invoice?.hostedInvoiceUrl);
              const invoiceUrl = invoice?.invoicePdfUrl || invoice?.hostedInvoiceUrl;

              return (
                <div
                  key={invoice.id}
                  className='border-1 border-300 border-round-lg p-3 flex flex-column gap-2 md:flex-row md:justify-content-between md:align-items-center'
                >
                  <div className='flex flex-column gap-1'>
                    <Typography className='font-semibold'>
                      {invoice.number || invoice.id}
                    </Typography>
                    <Typography className='text-700'>
                      {createdAt && isValid(createdAt)
                        ? format(createdAt, 'dd-MM-yyyy HH:mm')
                        : '-'}
                    </Typography>
                    <Typography className='text-700'>
                      {formatAmount(invoice.amountPaid, invoice.currency)}
                    </Typography>
                  </div>
                  <div>
                    <Button
                      label='Open invoice'
                      icon='pi pi-file-pdf'
                      disabled={!canOpen}
                      onClick={() => {
                        if (!invoiceUrl) return;
                        window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};

